/**
 * 请求选项
 */
interface RequestOptions extends RequestInit {
  /**
   * 查询参数，会自动附加到 URL 后面
   */
  params?: Record<string, string | number | boolean>;
  /**
   * 请求体数据，对于 POST, PUT 等方法
   */
  data?: unknown; // 使用 unknown 而不是 any
}

/**
 * 封装的响应对象
 */
interface FetchResponse<T = unknown> { // 使用泛型，默认为 unknown
  /**
   * 原始 Response 对象
   */
  rawResponse: Response;
  /**
   * 解析后的数据
   */
  data: T;
}

/**
 * 基础请求函数
 * @param url - 请求的 URL，可以是相对路径或绝对路径
 * @param options - 请求选项，继承自 RequestInit 并添加额外配置
 * @returns 返回包含原始响应和解析后数据的 Promise
 * @throws 如果网络请求失败或服务器返回错误状态码，则抛出错误
 */
async function request<T = unknown>(url: string, options: RequestOptions = {}): Promise<FetchResponse<T>> {
  const { params, data, headers: customHeaders, ...restOptions } = options;

  // 从环境变量读取 baseURL，如果未设置则默认为 ''
  // 注意：在浏览器环境中，需要确保构建工具正确注入了环境变量
  // 对于 Webpack 等构建工具，通常使用 DefinePlugin 或 EnvironmentPlugin 来注入 process.env
  const baseURL = process.env.APP_BASE_URL || '';

  let targetUrl = url;

  // 如果 url 不是绝对路径，则添加 baseURL
  if (!/^https?:\/\//.test(targetUrl)) {
    targetUrl = baseURL + targetUrl;
  }

  // 处理查询参数
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      targetUrl += (targetUrl.includes('?') ? '&' : '?') + queryString;
    }
  }

  // 使用 Headers 对象处理请求头
  const mergedHeaders = new Headers(customHeaders); // 从 customHeaders 初始化
  // 设置默认值（如果尚未设置）
  if (!mergedHeaders.has('Content-Type')) {
    mergedHeaders.set('Content-Type', 'application/json');
  }

  // 处理请求体
  let body: BodyInit | null = null;
  if (data !== undefined) {
    const contentType = mergedHeaders.get('Content-Type');
    // 如果 Content-Type 是 application/json，则序列化数据
    if (
      contentType && contentType.includes('application/json') &&
      typeof data === 'object' && data !== null
    ) {
      body = JSON.stringify(data);
    } else if (data instanceof FormData || data instanceof URLSearchParams || typeof data === 'string') {
      // 直接使用 FormData, URLSearchParams 或 string 作为 body
      body = data;
      // 如果是 FormData，浏览器会自动设置 Content-Type，需要移除我们设置的
      if (data instanceof FormData && mergedHeaders.has('Content-Type')) {
         mergedHeaders.delete('Content-Type');
      }
    } else {
      // 其他类型的数据转换为字符串
      body = String(data);
    }
  }

  try {
    const response = await fetch(targetUrl, {
      ...restOptions,
      headers: mergedHeaders,
      body: body,
    });

    if (!response.ok) {
      // 尝试解析错误响应体
      let errorData: unknown = null;
      try {
        errorData = await response.json();
      } catch (parseError) {
        // 如果解析失败，可能是非 JSON 响应
        console.warn('Failed to parse error response body as JSON:', parseError);
      }
      // 抛出一个包含状态码和可能解析出的错误数据的错误
      const error = new Error(`HTTP error! status: ${response.status}`);
      // 可以将 errorData 附加到 error 对象上，供上层捕获
      (error as any).data = errorData; // 使用 as any 临时规避类型检查，或者定义专门的错误类型
      (error as any).response = response;
      throw error;
    }

    // 解析 JSON 响应体
    // 注意：如果预期响应不是 JSON，需要调整这里的逻辑
    let responseData: T;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
    } else {
        // 对于非 JSON 响应，可以返回文本或 Blob 等
        // 这里我们假设调用者知道响应类型，并强制转换为 T
        // 或者可以根据 Content-Type 返回不同类型的值
        responseData = await response.text() as unknown as T; // 需要调用者确保类型匹配
    }


    return {
      rawResponse: response,
      data: responseData,
    };
  } catch (error) {
    console.error('Request failed:', error);
    // 重新抛出错误，统一处理
    throw error;
  }
}

/**
 * 发送 GET 请求
 * @param url - 请求 URL
 * @param params - 查询参数
 * @param options - 其他请求选项 (除了 method, params)
 * @returns 返回包含解析后数据的 Promise
 */
async function get<T = unknown>(url: string, params?: RequestOptions['params'], options: Omit<RequestOptions, 'params' | 'method' | 'body' | 'data'> = {}): Promise<T> {
  const response = await request<T>(url, {
    ...options,
    method: 'GET',
    params: params,
  });
  return response.data;
}

/**
 * 发送 POST 请求
 * @param url - 请求 URL
 * @param data - 请求体数据
 * @param options - 其他请求选项 (除了 method, data, body)
 * @returns 返回包含解析后数据的 Promise
 */
async function post<T = unknown>(url: string, data?: RequestOptions['data'], options: Omit<RequestOptions, 'data' | 'method' | 'body' | 'params'> = {}): Promise<T> {
 const response = await request<T>(url, {
    ...options,
    method: 'POST',
    data: data, // 使用 data 字段传递请求体
  });
  return response.data;
}

// 可以根据需要添加 put, delete 等方法
// async function put<T = unknown>(...) {}
// async function del<T = unknown>(...) {} // 使用 del 代替 delete，因为 delete 是关键字

export { request, get, post };
export type { RequestOptions, FetchResponse }; // 导出类型 